/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  ReactElement,
  Dispatch,
  useCallback,
  useEffect,
  useReducer,
} from "react";

import "./Applications.css";
import dayjs from "dayjs";
import { Section } from "react-bulma-components";

import { getPreparedRouteByAPIResponse } from "api/prepared_routes";
import {
  BulkApprovalDialog,
  Navbar,
  RouteApplicationsTable,
  TravelExpenseApplicationsTable,
  RouteApprovalDialog,
  SystemNotification,
  TravelExpenseApprovalDialog,
} from "components";
import { MoveMonthButtonGroup } from "components/atoms/Button";
import { GlobalErrorScreen } from "components/GlobalErrorScreen";
import { MODE as ROUTE_APPROVAL_DIALOG_MODE } from "components/RouteApprovalDialog/RouteApprovalDialog";
import { getFormattedComment } from "helpers/comment";
import {
  useGetApproverPreparedRouteApprovalsApi,
  useUpdateApproverPreparedRouteApprovalApi,
  useUpdateApproverMovementLogApprovalApi,
  useGetMovementLogApprovalsApi,
} from "hooks/api";
import { useQuery } from "hooks/router";
import {
  MovementLogApprovalListItem,
  getMovementLogApprovalByAPIResponse,
} from "models/movementLog";
import { PreparedRoute } from "models/preparedRoute";
import * as Layout from "molecules/Layout";

interface TravelExpenseApplicationsTableMonthlyProps {
  month: string;
  movementLogApprovals: MovementLogApprovalListItem[];
  dispatch: Dispatch<ApplicationsActions>;
}

function TravelExpenseApplicationsTableMonthly({
  month,
  movementLogApprovals,
  dispatch,
}: TravelExpenseApplicationsTableMonthlyProps): ReactElement | null {
  return (
    <div className="mb-4">
      <h1 className="subtitle is-6">
        承認月度：
        {month}
      </h1>
      <TravelExpenseApplicationsTable
        movementLogApprovals={movementLogApprovals}
        toggleBulkApprovalDialog={(applyingMovementLogApprovals) => {
          dispatch({
            type: "TOGGLE_TRAVEL_EXPENSE_BULK_APPROVAL_DIALOG",
            payload: {
              applyingMovementLogApprovals,
            },
          });
        }}
        toggleTravelExpenseApprovalDialogToApprove={(
          selectedMovementLogApproval
        ) => {
          dispatch({
            type: "TOGGLE_TRAVEL_EXPENSE_APPROVAL_DIALOG_TO_APPROVE",
            payload: {
              selectedMovementLogApproval,
            },
          });
        }}
        toggleTravelExpenseApprovalDialogToDeny={(
          selectedMovementLogApproval
        ) => {
          dispatch({
            type: "TOGGLE_TRAVEL_EXPENSE_APPROVAL_DIALOG_TO_DENY",
            payload: {
              selectedMovementLogApproval,
            },
          });
        }}
      />
    </div>
  );
}

export interface PreparedRouteApproval {
  id: number;
  preparedRoute: PreparedRoute;
  approvalStatus: string;
  approvalStatusName: string;
  applicantComment: string;
  approverComment: string;
}

interface ApplicationsState {
  preparedRouteApprovals: PreparedRouteApproval[];
  movementLogApprovals: MovementLogApprovalListItem[];
  isPreparedRouteApprovalsLoaded: boolean;

  isRouteApprovalDialogVisible: boolean;
  isUpdatePreparedRouteApprovalCompleted: boolean;
  isUpdatePreparedRouteApprovalInProgress: boolean;
  selectedPreparedRouteApproval?: PreparedRouteApproval;
  comment?: string;
  routeApprovalDialogMode?: string;
  travelExpenseApprovalDialogMode?: string;
  isUpdateMovementLogApprovalCompleted: boolean;
  isUpdateMovementLogApprovalInProgress: boolean;
  selectedMovementLogApproval?: MovementLogApprovalListItem;

  isBulkApprovalDialogVisible: boolean;
  isTravelExpenseBulkApprovalDialogVisible: boolean;
  isTravelExpenseApprovalDialogVisible: boolean;
  numApplications: number;
  applyingMovementLogApprovals: MovementLogApprovalListItem[];
}

const initialState: ApplicationsState = {
  preparedRouteApprovals: [],
  movementLogApprovals: [],
  isPreparedRouteApprovalsLoaded: false,
  isBulkApprovalDialogVisible: false,
  isTravelExpenseBulkApprovalDialogVisible: false,
  isTravelExpenseApprovalDialogVisible: false,
  isRouteApprovalDialogVisible: false,
  isUpdatePreparedRouteApprovalCompleted: false,
  isUpdatePreparedRouteApprovalInProgress: false,
  numApplications: 0,
  isUpdateMovementLogApprovalCompleted: false,
  isUpdateMovementLogApprovalInProgress: false,
  applyingMovementLogApprovals: [],
};

interface ChangeCommentAction {
  type: "CHANGE_COMMENT";
  payload: {
    comment: string;
  };
}

interface SetIsPreparedRouteApprovalsLoadedAction {
  type: "SET_IS_PREPARED_ROUTE_APPROVALS_LOADED";
  payload: {
    isPreparedRouteApprovalsLoaded: boolean;
  };
}

interface SetIsUpdatePreparedRouteApprovalCompletedAction {
  type: "SET_IS_UPDATE_PREPARED_ROUTE_APPROVAL_COMPLETED";
  payload: {
    isUpdatePreparedRouteApprovalCompleted: boolean;
    selectedPreparedRouteApproval?: PreparedRouteApproval;
  };
}

interface SetIsUpdatePreparedRouteApprovalInProgressAction {
  type: "SET_IS_UPDATE_PREPARED_ROUTE_APPROVAL_IN_PROGRESS";
  payload: {
    isUpdatePreparedRouteApprovalInProgress: boolean;
  };
}

interface SetIsUpdateMovementLogApprovalCompletedAction {
  type: "SET_IS_UPDATE_MOVEMENT_LOG_APPROVAL_COMPLETED";
  payload: {
    isUpdateMovementLogApprovalCompleted: boolean;
    selectedMovementLogApproval?: MovementLogApprovalListItem;
  };
}

interface SetIsUpdateMovementLogApprovalInProgressAction {
  type: "SET_IS_UPDATE_MOVEMENT_LOG_APPROVAL_IN_PROGRESS";
  payload: {
    isUpdateMovementLogApprovalInProgress: boolean;
  };
}

interface SetPreparedRouteApprovalAction {
  type: "SET_PREPARED_ROUTE_APPROVAL";
  payload: {
    preparedRouteApproval: PreparedRouteApproval;
  };
}

interface SetMovementLogApprovalAction {
  type: "SET_MOVEMENT_LOG_APPROVAL";
  payload: {
    movementLogApproval: MovementLogApprovalListItem;
  };
}

interface SetPreparedRouteApprovalsAction {
  type: "SET_PREPARED_ROUTE_APPROVALS";
  payload: {
    preparedRouteApprovals: PreparedRouteApproval[];
  };
}

interface SetMovementLogApprovalsAction {
  type: "SET_MOVEMENT_LOG_APPROVALS";
  payload: {
    movementLogApprovals: MovementLogApprovalListItem[];
  };
}

interface ToggleBulkApprovalDialogAction {
  type: "TOGGLE_BULK_APPROVAL_DIALOG";
}

interface ToggleTravelExpenseBulkApprovalDialogAction {
  type: "TOGGLE_TRAVEL_EXPENSE_BULK_APPROVAL_DIALOG";
  payload: {
    applyingMovementLogApprovals: MovementLogApprovalListItem[];
  };
}

interface ToggleRouteApprovalDialogAction {
  type: "TOGGLE_ROUTE_APPROVAL_DIALOG";
}

interface ToggleTravelExpenseApprovalDialogAction {
  type: "TOGGLE_TRAVEL_EXPENSE_APPROVAL_DIALOG";
}

interface ToggleRouteApprovalDialogToApproveAction {
  type: "TOGGLE_ROUTE_APPROVAL_DIALOG_TO_APPROVE";
  payload: {
    selectedPreparedRouteApproval: PreparedRouteApproval;
  };
}

interface ToggleRouteApprovalDialogToDenyAction {
  type: "TOGGLE_ROUTE_APPROVAL_DIALOG_TO_DENY";
  payload: {
    selectedPreparedRouteApproval: PreparedRouteApproval;
  };
}

interface ToggleTravelExpenseApprovalDialogToApproveAction {
  type: "TOGGLE_TRAVEL_EXPENSE_APPROVAL_DIALOG_TO_APPROVE";
  payload: {
    selectedMovementLogApproval: MovementLogApprovalListItem;
  };
}

interface ToggleTravelExpenseApprovalDialogToDenyAction {
  type: "TOGGLE_TRAVEL_EXPENSE_APPROVAL_DIALOG_TO_DENY";
  payload: {
    selectedMovementLogApproval: MovementLogApprovalListItem;
  };
}

interface ToggleTravelExpenseApprovalDialogAction {
  type: "TOGGLE_TRAVEL_EXPENSE_APPROVAL_DIALOG";
}

type ApplicationsActions =
  | ChangeCommentAction
  | SetIsPreparedRouteApprovalsLoadedAction
  | SetIsUpdatePreparedRouteApprovalCompletedAction
  | SetIsUpdatePreparedRouteApprovalInProgressAction
  | SetIsUpdateMovementLogApprovalCompletedAction
  | SetIsUpdateMovementLogApprovalInProgressAction
  | SetPreparedRouteApprovalAction
  | SetMovementLogApprovalAction
  | SetPreparedRouteApprovalsAction
  | SetMovementLogApprovalsAction
  | ToggleBulkApprovalDialogAction
  | ToggleTravelExpenseBulkApprovalDialogAction
  | ToggleRouteApprovalDialogAction
  | ToggleTravelExpenseApprovalDialogAction
  | ToggleRouteApprovalDialogToApproveAction
  | ToggleRouteApprovalDialogToDenyAction
  | ToggleTravelExpenseApprovalDialogToApproveAction
  | ToggleTravelExpenseApprovalDialogToDenyAction;

const reducer: React.Reducer<ApplicationsState, ApplicationsActions> = (
  currentState,
  action
) => {
  const newState: ApplicationsState = { ...currentState };

  switch (action.type) {
    case "CHANGE_COMMENT":
      newState.comment = action.payload.comment;
      break;
    case "SET_IS_PREPARED_ROUTE_APPROVALS_LOADED":
      newState.isPreparedRouteApprovalsLoaded =
        action.payload.isPreparedRouteApprovalsLoaded;
      break;
    case "SET_IS_UPDATE_PREPARED_ROUTE_APPROVAL_COMPLETED":
      newState.selectedPreparedRouteApproval =
        action.payload.selectedPreparedRouteApproval;
      newState.isUpdatePreparedRouteApprovalCompleted =
        action.payload.isUpdatePreparedRouteApprovalCompleted;
      break;
    case "SET_IS_UPDATE_PREPARED_ROUTE_APPROVAL_IN_PROGRESS":
      newState.isUpdatePreparedRouteApprovalInProgress =
        action.payload.isUpdatePreparedRouteApprovalInProgress;
      break;
    case "SET_IS_UPDATE_MOVEMENT_LOG_APPROVAL_COMPLETED":
      newState.selectedMovementLogApproval =
        action.payload.selectedMovementLogApproval;
      newState.isUpdateMovementLogApprovalCompleted =
        action.payload.isUpdateMovementLogApprovalCompleted;
      break;
    case "SET_IS_UPDATE_MOVEMENT_LOG_APPROVAL_IN_PROGRESS":
      newState.isUpdateMovementLogApprovalInProgress =
        action.payload.isUpdateMovementLogApprovalInProgress;
      break;
    case "SET_PREPARED_ROUTE_APPROVAL":
      newState.preparedRouteApprovals = newState.preparedRouteApprovals.map(
        (x) => {
          if (x.id === action.payload.preparedRouteApproval.id) {
            return action.payload.preparedRouteApproval;
          }
          return x;
        }
      );
      break;
    case "SET_MOVEMENT_LOG_APPROVAL":
      newState.movementLogApprovals = newState.movementLogApprovals.map((x) => {
        if (x.id === action.payload.movementLogApproval.id) {
          return action.payload.movementLogApproval;
        }
        return x;
      });
      break;
    case "SET_PREPARED_ROUTE_APPROVALS":
      newState.preparedRouteApprovals = action.payload.preparedRouteApprovals;
      break;
    case "SET_MOVEMENT_LOG_APPROVALS":
      newState.movementLogApprovals = action.payload.movementLogApprovals;
      break;
    case "TOGGLE_BULK_APPROVAL_DIALOG":
      newState.isBulkApprovalDialogVisible =
        !newState.isBulkApprovalDialogVisible;
      newState.isUpdatePreparedRouteApprovalCompleted = false;
      newState.isUpdatePreparedRouteApprovalInProgress = false;
      if (newState.isBulkApprovalDialogVisible) {
        newState.numApplications = newState.preparedRouteApprovals.filter(
          (preparedRouteApproval) =>
            preparedRouteApproval.approvalStatus === "applying"
        ).length;
      } else {
        newState.numApplications = 0;
      }
      break;
    case "TOGGLE_TRAVEL_EXPENSE_BULK_APPROVAL_DIALOG":
      newState.isTravelExpenseBulkApprovalDialogVisible =
        !newState.isTravelExpenseBulkApprovalDialogVisible;
      newState.isUpdateMovementLogApprovalCompleted = false;
      newState.isUpdateMovementLogApprovalInProgress = false;
      newState.applyingMovementLogApprovals =
        action.payload.applyingMovementLogApprovals;
      break;
    case "TOGGLE_ROUTE_APPROVAL_DIALOG":
      newState.isRouteApprovalDialogVisible = false;
      newState.isUpdatePreparedRouteApprovalCompleted = false;
      newState.isUpdatePreparedRouteApprovalInProgress = false;
      newState.selectedPreparedRouteApproval = undefined;
      newState.comment = undefined;
      newState.routeApprovalDialogMode = undefined;
      break;
    case "TOGGLE_TRAVEL_EXPENSE_APPROVAL_DIALOG":
      newState.isTravelExpenseApprovalDialogVisible = false;
      newState.isUpdateMovementLogApprovalCompleted = false;
      newState.isUpdateMovementLogApprovalInProgress = false;
      newState.selectedMovementLogApproval = undefined;
      newState.comment = undefined;
      newState.travelExpenseApprovalDialogMode = undefined;
      break;
    case "TOGGLE_ROUTE_APPROVAL_DIALOG_TO_APPROVE":
      newState.isRouteApprovalDialogVisible = true;
      newState.isUpdatePreparedRouteApprovalCompleted = false;
      newState.isUpdatePreparedRouteApprovalInProgress = false;
      newState.selectedPreparedRouteApproval =
        action.payload.selectedPreparedRouteApproval;
      newState.comment = "";
      newState.routeApprovalDialogMode = ROUTE_APPROVAL_DIALOG_MODE.approve;
      break;
    case "TOGGLE_ROUTE_APPROVAL_DIALOG_TO_DENY":
      newState.isRouteApprovalDialogVisible = true;
      newState.isUpdatePreparedRouteApprovalCompleted = false;
      newState.isUpdatePreparedRouteApprovalInProgress = false;
      newState.selectedPreparedRouteApproval =
        action.payload.selectedPreparedRouteApproval;
      newState.comment = "";
      newState.routeApprovalDialogMode = ROUTE_APPROVAL_DIALOG_MODE.deny;
      break;
    case "TOGGLE_TRAVEL_EXPENSE_APPROVAL_DIALOG_TO_APPROVE":
      newState.isTravelExpenseApprovalDialogVisible = true;
      newState.isUpdateMovementLogApprovalCompleted = false;
      newState.isUpdateMovementLogApprovalInProgress = false;
      newState.selectedMovementLogApproval =
        action.payload.selectedMovementLogApproval;
      newState.comment = "";
      newState.travelExpenseApprovalDialogMode =
        ROUTE_APPROVAL_DIALOG_MODE.approve;
      break;
    case "TOGGLE_TRAVEL_EXPENSE_APPROVAL_DIALOG_TO_DENY":
      newState.isTravelExpenseApprovalDialogVisible = true;
      newState.isUpdateMovementLogApprovalCompleted = false;
      newState.isUpdateMovementLogApprovalInProgress = false;
      newState.selectedMovementLogApproval =
        action.payload.selectedMovementLogApproval;
      newState.comment = "";
      newState.travelExpenseApprovalDialogMode =
        ROUTE_APPROVAL_DIALOG_MODE.deny;
      break;
    default:
      throw new Error();
  }
  return newState;
};

const getPreparedRouteApprovalByAPIResponse = (preparedRouteApproval: any) =>
  ({
    id: preparedRouteApproval.id,
    preparedRoute: getPreparedRouteByAPIResponse(
      preparedRouteApproval.prepared_route
    ),
    approvalStatus: preparedRouteApproval.approval_status,
    approvalStatusName: preparedRouteApproval.approval_status_name,
    applicantComment: preparedRouteApproval.applicant_comment,
    approverComment: preparedRouteApproval.approver_comment,
  } as PreparedRouteApproval);

const updateAllPreparedRouteApprovalToApprove = async (
  state: ApplicationsState,
  updateApi: ReturnType<typeof useUpdateApproverPreparedRouteApprovalApi>
) => {
  const { preparedRouteApprovals, comment } = state;

  const updatingPreparedRouteApprovals = preparedRouteApprovals.filter(
    (preparedRouteApproval) =>
      preparedRouteApproval.approvalStatus === "applying"
  );

  if (updatingPreparedRouteApprovals.length === 0)
    throw new Error("preparedRouteApproval is not selected.");

  const updatedPreparedRouteApprovals = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const preparedRouteApproval of updatingPreparedRouteApprovals) {
    updatedPreparedRouteApprovals.push(
      // eslint-disable-next-line no-await-in-loop
      await updateApi(
        preparedRouteApproval.id,
        "approved",
        getFormattedComment(preparedRouteApproval.approverComment, comment)
      )
    );
  }

  return updatedPreparedRouteApprovals;
};

const updateAllMovementLogApprovalToApprove = async (
  state: ApplicationsState,
  updateApi: ReturnType<typeof useUpdateApproverMovementLogApprovalApi>
) => {
  const { applyingMovementLogApprovals, comment } = state;

  const updatingMovementLogApprovals = applyingMovementLogApprovals.filter(
    (movementLogApproval) => movementLogApproval.approvalStatus === "applying"
  );

  if (updatingMovementLogApprovals.length === 0)
    throw new Error("movementLogApproval is not selected.");

  const updatedMovementLogApprovals = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const movementLogApproval of updatingMovementLogApprovals) {
    updatedMovementLogApprovals.push(
      // eslint-disable-next-line no-await-in-loop
      await updateApi(
        movementLogApproval.id,
        "approved",
        getFormattedComment(movementLogApproval.approverComment, comment)
      )
    );
  }

  return updatedMovementLogApprovals;
};

const updatePreparedRouteApprovalToApprove = async (
  state: ApplicationsState,
  updateApi: ReturnType<typeof useUpdateApproverPreparedRouteApprovalApi>
) => {
  const { selectedPreparedRouteApproval: preparedRouteApproval, comment } =
    state;

  if (!preparedRouteApproval)
    throw new Error("preparedRouteApproval is not selected.");

  return updateApi(
    preparedRouteApproval.id,
    "approved",
    getFormattedComment(preparedRouteApproval.approverComment, comment)
  );
};

const updatePreparedRouteApprovalToDeny = async (
  state: ApplicationsState,
  updateApi: ReturnType<typeof useUpdateApproverPreparedRouteApprovalApi>
) => {
  const { selectedPreparedRouteApproval: preparedRouteApproval, comment } =
    state;

  if (!preparedRouteApproval)
    throw new Error("preparedRouteApproval is not selected.");

  return updateApi(
    preparedRouteApproval.id,
    "unapplied",
    getFormattedComment(preparedRouteApproval.approverComment, comment)
  );
};

const updateMovementLogApprovalToApprove = async (
  state: ApplicationsState,
  updateApi: ReturnType<typeof useUpdateApproverMovementLogApprovalApi>
) => {
  const { selectedMovementLogApproval: movementLogApproval, comment } = state;

  if (!movementLogApproval)
    throw new Error("movementLogApproval is not selected.");

  return updateApi(
    movementLogApproval.id,
    "approved",
    getFormattedComment(movementLogApproval.approverComment, comment)
  );
};

const updateMovementLogApprovalToDeny = async (
  state: ApplicationsState,
  updateApi: ReturnType<typeof useUpdateApproverMovementLogApprovalApi>
) => {
  const { selectedMovementLogApproval: movementLogApproval, comment } = state;

  if (!movementLogApproval)
    throw new Error("movementLogApproval is not selected.");

  return updateApi(
    movementLogApproval.id,
    "unapplied",
    getFormattedComment(movementLogApproval.approverComment, comment)
  );
};

const groupBy = <K, V>(
  array: readonly V[],
  getKey: (cur: V, idx: number, src: readonly V[]) => K
): [K, V[]][] =>
  Array.from(
    array.reduce((map, cur, idx, src) => {
      const key = getKey(cur, idx, src);
      const list = map.get(key);
      if (list) list.push(cur);
      else map.set(key, [cur]);
      return map;
    }, new Map<K, V[]>())
  );

function ApplicationsContent(): ReactElement | null {
  const query = useQuery();

  const [state, dispatch] = useReducer(reducer, initialState);
  const getApproverPreparedRouteApprovalsApi =
    useGetApproverPreparedRouteApprovalsApi();
  const updateApproverPreparedRouteApprovalApi =
    useUpdateApproverPreparedRouteApprovalApi();
  const getMovementLogApprovalsApi = useGetMovementLogApprovalsApi();
  const updateApproverMovementLogApprovalApi =
    useUpdateApproverMovementLogApprovalApi();

  useEffect(() => {
    (async () => {
      try {
        const data = await getApproverPreparedRouteApprovalsApi();
        const preparedRouteApprovals = data.reduce(
          (acc: PreparedRouteApproval[], preparedRouteApproval: any) => [
            ...acc,
            getPreparedRouteApprovalByAPIResponse(preparedRouteApproval),
          ],
          []
        );

        dispatch({
          type: "SET_PREPARED_ROUTE_APPROVALS",
          payload: {
            preparedRouteApprovals,
          },
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
      } finally {
        dispatch({
          type: "SET_IS_PREPARED_ROUTE_APPROVALS_LOADED",
          payload: {
            isPreparedRouteApprovalsLoaded: true,
          },
        });
      }
    })();
  }, [getApproverPreparedRouteApprovalsApi]);

  useEffect(() => {
    (async () => {
      const movementLogApprovalsApiResponse = await getMovementLogApprovalsApi(
        query.get("month")
      );
      const movementLogApprovals = movementLogApprovalsApiResponse.reduce(
        (acc: MovementLogApprovalListItem[], movementLogApproval: any) => [
          ...acc,
          getMovementLogApprovalByAPIResponse(movementLogApproval),
        ],
        []
      );
      dispatch({
        type: "SET_MOVEMENT_LOG_APPROVALS",
        payload: {
          movementLogApprovals,
        },
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getMovementLogApprovalsApi]);

  // 申請月度でのグルーピング
  const movementLogApprovalGroupByMonth = groupBy(
    state.movementLogApprovals,
    (item) => dayjs(item.approvalMonth).format("YYYY年MM月度")
  );
  // 申請月度の降順
  movementLogApprovalGroupByMonth.sort(([a], [b]) => (a > b ? -1 : 1));

  return (
    <>
      <header>
        <Navbar />
      </header>
      <main role="main" className="container">
        <Section>
          <SystemNotification />
          <Layout.FlexWrapper alignItems="flex-start">
            <h2 className="title is-2">承認待ち申請（通勤費・交通費）</h2>
            <MoveMonthButtonGroup untilThisMonth />
          </Layout.FlexWrapper>
          {movementLogApprovalGroupByMonth.map(
            ([month, movementLogApprovals]) => (
              <TravelExpenseApplicationsTableMonthly
                key={month}
                month={month}
                movementLogApprovals={movementLogApprovals}
                dispatch={dispatch}
              />
            )
          )}
          {movementLogApprovalGroupByMonth.length <= 0 && (
            <div className="mb-4">
              <div>承認待ち申請はありません</div>
            </div>
          )}
          <h2 className="title is-2">承認待ち申請（通勤経路）</h2>
          <RouteApplicationsTable
            preparedRouteApprovals={state.preparedRouteApprovals}
            toggleBulkApprovalDialog={() => {
              dispatch({
                type: "TOGGLE_BULK_APPROVAL_DIALOG",
              });
            }}
            toggleRouteApprovalDialogToApprove={(
              selectedPreparedRouteApproval
            ) => {
              dispatch({
                type: "TOGGLE_ROUTE_APPROVAL_DIALOG_TO_APPROVE",
                payload: {
                  selectedPreparedRouteApproval,
                },
              });
            }}
            toggleRouteApprovalDialogToDeny={(
              selectedPreparedRouteApproval
            ) => {
              dispatch({
                type: "TOGGLE_ROUTE_APPROVAL_DIALOG_TO_DENY",
                payload: {
                  selectedPreparedRouteApproval,
                },
              });
            }}
          />
        </Section>
        <BulkApprovalDialog
          name="通勤経路"
          isVisible={state.isBulkApprovalDialogVisible}
          isUpdateCompleted={state.isUpdatePreparedRouteApprovalCompleted}
          isUpdateInProgress={state.isUpdatePreparedRouteApprovalInProgress}
          numApplications={state.numApplications}
          toggleDialog={() => {
            dispatch({
              type: "TOGGLE_BULK_APPROVAL_DIALOG",
            });
          }}
          updateAllToApprove={useCallback(async () => {
            dispatch({
              type: "SET_IS_UPDATE_PREPARED_ROUTE_APPROVAL_IN_PROGRESS",
              payload: { isUpdatePreparedRouteApprovalInProgress: true },
            });

            try {
              const updatedPreparedRouteApprovals =
                await updateAllPreparedRouteApprovalToApprove(
                  state,
                  updateApproverPreparedRouteApprovalApi
                );

              const preparedRouteApprovals = [...state.preparedRouteApprovals];
              for (let i = 0; i < preparedRouteApprovals.length; i += 1) {
                for (
                  let j = 0;
                  j < updatedPreparedRouteApprovals.length;
                  j += 1
                ) {
                  const updatedPreparedRouteApproval =
                    getPreparedRouteApprovalByAPIResponse(
                      updatedPreparedRouteApprovals[j]
                    );

                  if (
                    preparedRouteApprovals[i].id ===
                    updatedPreparedRouteApproval.id
                  ) {
                    preparedRouteApprovals[i] = updatedPreparedRouteApproval;
                  }
                }
              }

              dispatch({
                type: "SET_PREPARED_ROUTE_APPROVALS",
                payload: { preparedRouteApprovals },
              });

              dispatch({
                type: "SET_IS_UPDATE_PREPARED_ROUTE_APPROVAL_COMPLETED",
                payload: {
                  isUpdatePreparedRouteApprovalCompleted: true,
                },
              });
            } catch (error) {
              // eslint-disable-next-line no-console
              console.log(error);
            } finally {
              dispatch({
                type: "SET_IS_UPDATE_PREPARED_ROUTE_APPROVAL_IN_PROGRESS",
                payload: { isUpdatePreparedRouteApprovalInProgress: false },
              });
            }
          }, [state, updateApproverPreparedRouteApprovalApi])}
        />
        {/* 通勤費・交通費一括承認 */}
        <BulkApprovalDialog
          name="通勤費・交通費"
          isVisible={state.isTravelExpenseBulkApprovalDialogVisible}
          isUpdateCompleted={state.isUpdateMovementLogApprovalCompleted}
          isUpdateInProgress={state.isUpdateMovementLogApprovalInProgress}
          numApplications={state.applyingMovementLogApprovals.length}
          toggleDialog={() => {
            dispatch({
              type: "TOGGLE_TRAVEL_EXPENSE_BULK_APPROVAL_DIALOG",
              payload: {
                applyingMovementLogApprovals: [],
              },
            });
          }}
          updateAllToApprove={useCallback(async () => {
            dispatch({
              type: "SET_IS_UPDATE_MOVEMENT_LOG_APPROVAL_IN_PROGRESS",
              payload: { isUpdateMovementLogApprovalInProgress: true },
            });

            try {
              const updatedMovementLogApprovals =
                await updateAllMovementLogApprovalToApprove(
                  state,
                  updateApproverMovementLogApprovalApi
                );

              const movementLogApprovals = [...state.movementLogApprovals];
              for (let i = 0; i < movementLogApprovals.length; i += 1) {
                for (
                  let j = 0;
                  j < updatedMovementLogApprovals.length;
                  j += 1
                ) {
                  const updatedMovementLogApproval =
                    getMovementLogApprovalByAPIResponse(
                      updatedMovementLogApprovals[j]
                    );

                  if (
                    movementLogApprovals[i].id === updatedMovementLogApproval.id
                  ) {
                    movementLogApprovals[i] = updatedMovementLogApproval;
                  }
                }
              }

              dispatch({
                type: "SET_MOVEMENT_LOG_APPROVALS",
                payload: { movementLogApprovals },
              });

              dispatch({
                type: "SET_IS_UPDATE_MOVEMENT_LOG_APPROVAL_COMPLETED",
                payload: {
                  isUpdateMovementLogApprovalCompleted: true,
                },
              });
            } finally {
              dispatch({
                type: "SET_IS_UPDATE_MOVEMENT_LOG_APPROVAL_IN_PROGRESS",
                payload: { isUpdateMovementLogApprovalInProgress: false },
              });
            }
          }, [state, updateApproverMovementLogApprovalApi])}
        />
        <RouteApprovalDialog
          isVisible={state.isRouteApprovalDialogVisible}
          isUpdateCompleted={state.isUpdatePreparedRouteApprovalCompleted}
          isUpdateInProgress={state.isUpdatePreparedRouteApprovalInProgress}
          preparedRouteApproval={state.selectedPreparedRouteApproval}
          comment={state.comment}
          mode={state.routeApprovalDialogMode}
          toggleDialog={() => {
            dispatch({
              type: "TOGGLE_ROUTE_APPROVAL_DIALOG",
            });
          }}
          changeComment={(comment) => {
            dispatch({
              type: "CHANGE_COMMENT",
              payload: { comment },
            });
          }}
          updateToApprove={useCallback(async () => {
            dispatch({
              type: "SET_IS_UPDATE_PREPARED_ROUTE_APPROVAL_IN_PROGRESS",
              payload: { isUpdatePreparedRouteApprovalInProgress: true },
            });

            try {
              const preparedRouteApproval =
                getPreparedRouteApprovalByAPIResponse(
                  await updatePreparedRouteApprovalToApprove(
                    state,
                    updateApproverPreparedRouteApprovalApi
                  )
                );

              const selectedPreparedRouteApproval = {
                ...state.selectedPreparedRouteApproval,
              };
              selectedPreparedRouteApproval.approvalStatusName = "承認済み";

              dispatch({
                type: "SET_PREPARED_ROUTE_APPROVAL",
                payload: { preparedRouteApproval },
              });

              dispatch({
                type: "SET_IS_UPDATE_PREPARED_ROUTE_APPROVAL_COMPLETED",
                payload: {
                  isUpdatePreparedRouteApprovalCompleted: true,
                  selectedPreparedRouteApproval:
                    selectedPreparedRouteApproval as PreparedRouteApproval,
                },
              });
            } catch (error) {
              // eslint-disable-next-line no-console
              console.log(error);
            } finally {
              dispatch({
                type: "SET_IS_UPDATE_PREPARED_ROUTE_APPROVAL_IN_PROGRESS",
                payload: { isUpdatePreparedRouteApprovalInProgress: false },
              });
            }
          }, [state, updateApproverPreparedRouteApprovalApi])}
          updateToDeny={useCallback(async () => {
            dispatch({
              type: "SET_IS_UPDATE_PREPARED_ROUTE_APPROVAL_IN_PROGRESS",
              payload: { isUpdatePreparedRouteApprovalInProgress: true },
            });

            try {
              const preparedRouteApproval =
                getPreparedRouteApprovalByAPIResponse(
                  await updatePreparedRouteApprovalToDeny(
                    state,
                    updateApproverPreparedRouteApprovalApi
                  )
                );

              const selectedPreparedRouteApproval = {
                ...state.selectedPreparedRouteApproval,
              };
              selectedPreparedRouteApproval.approvalStatusName = "未申請";

              dispatch({
                type: "SET_PREPARED_ROUTE_APPROVAL",
                payload: { preparedRouteApproval },
              });

              dispatch({
                type: "SET_IS_UPDATE_PREPARED_ROUTE_APPROVAL_COMPLETED",
                payload: {
                  isUpdatePreparedRouteApprovalCompleted: true,
                  selectedPreparedRouteApproval:
                    selectedPreparedRouteApproval as PreparedRouteApproval,
                },
              });
            } catch (error) {
              // eslint-disable-next-line no-console
              console.log(error);
            } finally {
              dispatch({
                type: "SET_IS_UPDATE_PREPARED_ROUTE_APPROVAL_IN_PROGRESS",
                payload: { isUpdatePreparedRouteApprovalInProgress: false },
              });
            }
          }, [state, updateApproverPreparedRouteApprovalApi])}
        />
        <TravelExpenseApprovalDialog
          isVisible={state.isTravelExpenseApprovalDialogVisible}
          isUpdateCompleted={state.isUpdateMovementLogApprovalCompleted}
          isUpdateInProgress={state.isUpdateMovementLogApprovalInProgress}
          movementLogApproval={state.selectedMovementLogApproval}
          comment={state.comment}
          mode={state.travelExpenseApprovalDialogMode}
          toggleDialog={() => {
            dispatch({
              type: "TOGGLE_TRAVEL_EXPENSE_APPROVAL_DIALOG",
            });
          }}
          changeComment={(comment) => {
            dispatch({
              type: "CHANGE_COMMENT",
              payload: { comment },
            });
          }}
          updateToApprove={useCallback(async () => {
            dispatch({
              type: "SET_IS_UPDATE_MOVEMENT_LOG_APPROVAL_IN_PROGRESS",
              payload: { isUpdateMovementLogApprovalInProgress: true },
            });

            try {
              const movementLogApproval = getMovementLogApprovalByAPIResponse(
                await updateMovementLogApprovalToApprove(
                  state,
                  updateApproverMovementLogApprovalApi
                )
              );

              const selectedMovementLogApproval = {
                ...state.selectedMovementLogApproval,
              };
              selectedMovementLogApproval.approvalStatusName = "承認済み";

              dispatch({
                type: "SET_MOVEMENT_LOG_APPROVAL",
                payload: { movementLogApproval },
              });

              dispatch({
                type: "SET_IS_UPDATE_MOVEMENT_LOG_APPROVAL_COMPLETED",
                payload: {
                  isUpdateMovementLogApprovalCompleted: true,
                  selectedMovementLogApproval:
                    selectedMovementLogApproval as MovementLogApprovalListItem,
                },
              });
            } finally {
              dispatch({
                type: "SET_IS_UPDATE_MOVEMENT_LOG_APPROVAL_IN_PROGRESS",
                payload: { isUpdateMovementLogApprovalInProgress: false },
              });
            }
          }, [state, updateApproverMovementLogApprovalApi])}
          updateToDeny={useCallback(async () => {
            dispatch({
              type: "SET_IS_UPDATE_MOVEMENT_LOG_APPROVAL_IN_PROGRESS",
              payload: { isUpdateMovementLogApprovalInProgress: true },
            });

            try {
              const movementLogApproval = getMovementLogApprovalByAPIResponse(
                await updateMovementLogApprovalToDeny(
                  state,
                  updateApproverMovementLogApprovalApi
                )
              );

              const selectedMovementLogApproval = {
                ...state.selectedMovementLogApproval,
              };
              selectedMovementLogApproval.approvalStatusName = "未申請";

              dispatch({
                type: "SET_MOVEMENT_LOG_APPROVAL",
                payload: { movementLogApproval },
              });

              dispatch({
                type: "SET_IS_UPDATE_MOVEMENT_LOG_APPROVAL_COMPLETED",
                payload: {
                  isUpdateMovementLogApprovalCompleted: true,
                  selectedMovementLogApproval:
                    selectedMovementLogApproval as MovementLogApprovalListItem,
                },
              });
            } finally {
              dispatch({
                type: "SET_IS_UPDATE_MOVEMENT_LOG_APPROVAL_IN_PROGRESS",
                payload: { isUpdateMovementLogApprovalInProgress: false },
              });
            }
          }, [state, updateApproverMovementLogApprovalApi])}
        />
      </main>
    </>
  );
}

export function Applications(): ReactElement | null {
  return (
    <GlobalErrorScreen>
      <ApplicationsContent />
    </GlobalErrorScreen>
  );
}
/* eslint-enable */
